import React, {
  useContext,
  useEffect,
  useState
} from "react";

import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography
} from "@material-ui/core";

import {
  makeStyles
} from "@material-ui/core/styles";

import usePlans from "../../../hooks/usePlans";

import {
  AuthContext
} from "../../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column"
  },

  cardHeader: {
    backgroundColor:
      theme.palette.type === "light"
        ? theme.palette.grey[200]
        : theme.palette.grey[700]
  },

  cardContent: {
    flexGrow: 1,
    textAlign: "center"
  },

  intro: {
    marginBottom: theme.spacing(2)
  },

  price: {
    fontWeight: 700,
    marginTop: theme.spacing(1)
  },

  discount: {
    fontWeight: 700,
    marginTop: theme.spacing(1)
  },

  oldPrice: {
    textDecoration: "line-through",
    opacity: 0.65
  },

  monthly: {
    marginTop: theme.spacing(1)
  }
}));

const parseMoney = value => {
  if (typeof value === "number") {
    return Number.isFinite(value)
      ? value
      : 0;
  }

  if (
    value === null ||
    value === undefined
  ) {
    return 0;
  }

  let text = String(value)
    .trim()
    .replace(/\s/g, "");

  if (text.includes(",")) {
    text = text
      .replace(/\./g, "")
      .replace(",", ".");
  }

  const parsed = Number(text);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
};

const roundMoney = value =>
  Math.round(
    (
      Number(value) +
      Number.EPSILON
    ) * 100
  ) / 100;

const formatMoney = value =>
  Number(value || 0)
    .toLocaleString(
      "pt-BR",
      {
        style: "currency",
        currency: "BRL"
      }
    );

export default function PaymentForm(props) {
  const {
    setFieldValue,
    setActiveStep,
    activeStep,
    Invoice
  } = props;

  const classes = useStyles();

  const {
    user
  } = useContext(AuthContext);

  const {
    getPlanCompany
  } = usePlans();

  const [
    plan,
    setPlan
  ] = useState(null);

  const [
    loading,
    setLoading
  ] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadPlan() {
      try {
        const response =
          await getPlanCompany(
            undefined,
            user.companyId
          );

        if (mounted) {
          setPlan(
            response.plan || null
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadPlan();

    return () => {
      mounted = false;
    };

    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <Grid
        container
        justifyContent="center"
      >
        <CircularProgress />
      </Grid>
    );
  }

  if (!plan) {
    return (
      <Typography
        color="error"
        align="center"
      >
        Não foi possível carregar
        o plano desta empresa.
      </Typography>
    );
  }

  // Usa o valor individual da fatura.
  // Assim contratos personalizados são preservados.
  const invoiceBase =
    parseMoney(
      Invoice &&
      Invoice.baseValue !== null &&
      Invoice.baseValue !== undefined
        ? Invoice.baseValue
        : Invoice && Invoice.value
    );

  const planBase =
    parseMoney(
      plan.amount
    );

  const baseValue =
    invoiceBase > 0
      ? invoiceBase
      : planBase;

  const semiDiscount =
    Math.max(
      0,
      Math.min(
        99,
        Number(
          plan.semiannualDiscountPercent ||
          0
        )
      )
    );

  const annualDiscount =
    Math.max(
      0,
      Math.min(
        99,
        Number(
          plan.annualDiscountPercent ||
          0
        )
      )
    );

  const isStorageInvoice =
    Invoice?.billingType ===
    "storage";

  const periods =
    isStorageInvoice
      ? [
          {
            months: 1,
            title: "1 mês",
            discount: 0
          }
        ]
      : [
          {
            months: 1,
            title: "1 mês",
            discount: 0
          },

          {
            months: 6,
            title: "6 meses",
            discount: semiDiscount
          },

          {
            months: 12,
            title: "12 meses",
            discount: annualDiscount
          }
        ];

  const selectPeriod = item => {
    const gross =
      roundMoney(
        baseValue *
        item.months
      );

    const total =
      roundMoney(
        gross *
        (
          1 -
          item.discount / 100
        )
      );

    const selectedPlan = {
      title:
        isStorageInvoice
          ? Invoice?.detail ||
            "Armazenamento extra"
          : plan.name,
      planId: plan.id,

      // Compatibilidade com checkout antigo.
      // Backend nao usa este valor como fonte de verdade.
      price: total,

      monthlyPrice:
        baseValue,

      users:
        plan.users,

      connections:
        plan.connections,

      queues:
        plan.queues,

      billingMonths:
        item.months,

      discountPercent:
        item.discount,

      gross,
      total
    };

    setFieldValue(
      "plan",
      JSON.stringify(
        selectedPlan
      )
    );

    setFieldValue(
      "billingMonths",
      item.months
    );

    setActiveStep(
      activeStep + 1
    );
  };

  return (
    <React.Fragment>
      <Typography
        variant="h6"
        align="center"
        className={classes.intro}
      >
        {isStorageInvoice
          ? "Armazenamento extra com cobrança mensal"
          : "Escolha por quanto tempo deseja antecipar sua assinatura"}
      </Typography>

      <Typography
        variant="body2"
        align="center"
        className={classes.intro}
      >
        {isStorageInvoice
          ? "Valor mensal do armazenamento: "
          : "Valor mensal da sua assinatura: "}
        <strong>
          {formatMoney(baseValue)}
        </strong>
      </Typography>

      <Grid
        container
        spacing={2}
      >
        {periods.map(item => {
          const gross =
            roundMoney(
              baseValue *
              item.months
            );

          const total =
            roundMoney(
              gross *
              (
                1 -
                item.discount / 100
              )
            );

          const perMonth =
            roundMoney(
              total /
              item.months
            );

          return (
            <Grid
              item
              xs={12}
              md={4}
              key={item.months}
            >
              <Card
                variant="outlined"
                className={classes.card}
              >
                <CardHeader
                  title={item.title}
                  className={
                    classes.cardHeader
                  }
                  titleTypographyProps={{
                    align: "center"
                  }}
                />

                <CardContent
                  className={
                    classes.cardContent
                  }
                >
                  {item.discount > 0 && (
                    <Typography
                      variant="body2"
                      className={
                        classes.oldPrice
                      }
                    >
                      {formatMoney(gross)}
                    </Typography>
                  )}

                  <Typography
                    variant="h5"
                    className={
                      classes.price
                    }
                  >
                    {formatMoney(total)}
                  </Typography>

                  {item.discount > 0 && (
                    <Typography
                      color="primary"
                      className={
                        classes.discount
                      }
                    >
                      {item.discount}% de desconto
                    </Typography>
                  )}

                  {item.months > 1 &&
                    item.discount === 0 && (
                      <Typography
                        color="textSecondary"
                        variant="body2"
                        className={
                          classes.discount
                        }
                      >
                        Sem desconto configurado
                      </Typography>
                    )}

                  {item.months > 1 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={
                        classes.monthly
                      }
                    >
                      Equivale a{" "}
                      {formatMoney(perMonth)}
                      /mês
                    </Typography>
                  )}

                  {item.months === 1 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={
                        classes.monthly
                      }
                    >
                      Pagamento mensal
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      selectPeriod(item)
                    }
                  >
                    SELECIONAR
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </React.Fragment>
  );
}
